import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Calendar, Clock, MapPin, Users, Award, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';

const BootcampsPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const bootcamps = [
    {
      id: 1,
      icon: '🖥️',
      title: 'IT Fundamentals Bootcamp',
      duration: '2 Hours',
      description: 'Learn essential computer and networking concepts every tech professional should know.',
      agenda: [
        'Introduction to IT Support Roles',
        'Operating Systems & Networking Basics',
        'Troubleshooting & Cloud Overview'
      ],
      outcome: 'Build confidence to start a career in IT or prepare for entry-level certifications.',
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 2,
      icon: '🤖',
      title: 'AI Job-Ready Bootcamp',
      duration: '2 Hours',
      description: 'Get a crash course in real-world AI applications and career pathways.',
      agenda: [
        'How AI Works — From Data to Decisions',
        'Building with ChatGPT & Prompt Engineering',
        'Exploring Real AI Job Roles & Tools (AWS Bedrock, Azure AI, LangChain)'
      ],
      outcome: 'Understand AI in action and identify your AI career roadmap.',
      color: 'from-purple-500 to-purple-600'
    },
    {
      id: 3,
      icon: '🚀',
      title: 'Career Accelerator Bootcamp',
      duration: '2 Hours',
      description: 'Learn how to present your skills and land your first tech job.',
      agenda: [
        'Building a Job-Winning Resume',
        'Mastering Technical Interviews',
        'LinkedIn & Portfolio Optimization'
      ],
      outcome: 'Leave with a refined resume, a LinkedIn profile strategy, and clarity on your next career step.',
      color: 'from-indigo-500 to-indigo-600'
    }
  ];

  const schedule = [
    {
      title: 'IT Fundamentals',
      date: 'Monday, 28 Oct 2025',
      time: '5:00 PM – 7:00 PM',
      venue: 'CareerLift Training Hub, Sydney CBD',
      available: true
    },
    {
      title: 'AI Job-Ready',
      date: 'Saturday, 15 Nov 2025',
      time: '10:00 AM – 12:00 PM',
      venue: 'CareerLift Training Hub, Sydney CBD',
      available: true
    },
    {
      title: 'Career Accelerator',
      date: 'Friday, 22 Nov 2025',
      time: '6:00 PM – 8:00 PM',
      venue: 'CareerLift Training Hub, Sydney CBD',
      available: true
    }
  ];

  const faqs = [
    {
      question: "Where are the sessions held?",
      answer: "This is online with face-to-face career discussion. You'll receive the session link and details in your confirmation email after registration."
    },
    {
      question: "What should I bring?",
      answer: "At home, have laptop and Internet connection."
    },
    {
      question: "Who can attend?",
      answer: "Anyone aspiring to start or grow a tech career can join — whether you're a student, recent graduate, or early professional looking to understand IT and AI fundamentals."
    },
    {
      question: "Are certificates provided?",
      answer: "Yes! You'll receive a CareerLift Certificate of Participation upon completion, highlighting your practical learning and new skills."
    },
    {
      question: "How do I enroll?",
      answer: "Click 'Register Now' on your chosen bootcamp card, fill out the short registration form, and you'll receive an email confirmation within minutes."
    }
  ];

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <Header />

      {/* Hero Section */}
      <section className="pt-20 pb-16 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block mb-6">
            <span className="bg-white/20 text-white px-6 py-2 rounded-full text-sm font-semibold backdrop-blur-sm">
              CareerLift Bootcamps
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            2 Hours That Can Change Your Career.
          </h1>
          <p className="text-xl md:text-2xl text-blue-100 font-light max-w-3xl mx-auto mb-8">
            Join our online bootcamps with face-to-face career discussions and experience hands-on IT and AI training that's designed to make you job-ready fast.
          </p>
          <Button
            size="lg"
            className="bg-white text-indigo-700 hover:bg-gray-100 font-semibold px-10 py-6 text-lg rounded-xl shadow-xl"
            onClick={() => document.getElementById('schedule').scrollIntoView({ behavior: 'smooth' })}
          >
            View Upcoming Bootcamps
          </Button>
        </div>
      </section>

      {/* Subtitle Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            CareerLift Bootcamps – 2-Hour Intensive Learning Sessions in Sydney
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Fast, practical, and job-focused learning — master core IT and AI skills in just 2 hours.
          </p>
        </div>
      </section>

      {/* Bootcamp Types */}
      <section className="py-16 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Choose Your Bootcamp
            </h2>
            <p className="text-xl text-slate-600">
              Three focused sessions designed to accelerate your tech career
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {bootcamps.map((bootcamp) => (
              <div key={bootcamp.id} className="bg-white rounded-3xl shadow-lg overflow-hidden border-2 border-gray-100 hover:border-indigo-200 transition-all hover:shadow-xl">
                <div className={`bg-gradient-to-r ${bootcamp.color} p-6 text-white`}>
                  <div className="text-6xl mb-4">{bootcamp.icon}</div>
                  <h3 className="text-2xl font-bold mb-2">{bootcamp.title}</h3>
                  <div className="flex items-center gap-2 text-white/90">
                    <Clock className="w-4 h-4" />
                    <span>{bootcamp.duration}</span>
                  </div>
                </div>

                <div className="p-6">
                  <p className="text-slate-700 mb-6">{bootcamp.description}</p>

                  <div className="mb-6">
                    <h4 className="font-bold text-slate-900 mb-3">Agenda:</h4>
                    <ul className="space-y-2">
                      {bootcamp.agenda.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-indigo-50 rounded-xl p-4 mb-6">
                    <h4 className="font-bold text-indigo-900 mb-2 flex items-center gap-2">
                      <Award className="w-5 h-5" />
                      Outcome
                    </h4>
                    <p className="text-sm text-indigo-700">{bootcamp.outcome}</p>
                  </div>

                  <Button
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 rounded-xl"
                    onClick={() => window.location.href = '/contact'}
                  >
                    Book Your Seat
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Schedule Block */}
      <section id="schedule" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Next Online Bootcamps
            </h2>
            <p className="text-xl text-slate-600">
              Reserve your spot now — limited seats available
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {schedule.map((session, idx) => (
              <div key={idx} className="bg-white rounded-2xl border-2 border-indigo-200 p-8 hover:shadow-xl transition-all">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">{session.title}</h3>
                  
                  <div className="space-y-3 text-slate-600">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-indigo-600" />
                      <span>{session.date}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-indigo-600" />
                      <span>{session.time}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                      <span>{session.venue}</span>
                    </div>
                  </div>
                </div>

                {session.available ? (
                  <div>
                    <div className="flex items-center gap-2 text-green-600 mb-4">
                      <Users className="w-5 h-5" />
                      <span className="font-semibold">Seats Available</span>
                    </div>
                    <Button
                      className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 rounded-xl"
                      onClick={() => window.location.href = '/contact'}
                    >
                      Register Now
                    </Button>
                  </div>
                ) : (
                  <div className="text-center">
                    <span className="text-gray-500">Sold Out</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about our bootcamps
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-gray-50 rounded-2xl overflow-hidden border-2 border-gray-200 hover:border-blue-400 transition-all">
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
                >
                  <span className="text-lg font-bold text-gray-900 pr-4">{faq.question}</span>
                  {openFaq === idx ? (
                    <ChevronUp className="w-6 h-6 text-blue-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-6 h-6 text-gray-600 flex-shrink-0" />
                  )}
                </button>
                {openFaq === idx && (
                  <div className="px-6 py-5 bg-white border-t-2 border-gray-200">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">Still have questions?</p>
            <button
              onClick={() => window.location.href = '/contact'}
              className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold px-8 py-3 rounded-xl hover:shadow-lg transition-all"
            >
              Book a Free Consultation
            </button>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-br from-indigo-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Transform Your Career in 2 Hours?
          </h2>
          <p className="text-xl text-indigo-100 mb-8">
            Join hundreds of students who've accelerated their tech careers with CareerLift bootcamps
          </p>
          <Button
            size="lg"
            className="bg-white text-indigo-700 hover:bg-gray-100 font-semibold px-10 py-6 text-lg rounded-xl shadow-xl"
            onClick={() => document.getElementById('schedule').scrollIntoView({ behavior: 'smooth' })}
          >
            View Schedule & Register
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BootcampsPage;
